import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { superAdminAPI } from '../../services/api';
import {
  ExportMenu, FilterSelect, ReportFilterBar, EMPTY_REPORT_FILTERS, filterReportRows,
} from '../../components/SuperAdmin/ListTools';
import {
  HiOutlineClipboardCheck,
  HiOutlineRefresh,
} from 'react-icons/hi';
import { ReportGuide, periodLabel } from '../../components/SuperAdmin/ReportGuide';

const PERIODS = [7, 30, 90, 180, 365];

const PRIORITY_LABELS = {
  urgent: 'Urgent',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

const PRIORITY_COLORS = {
  urgent: 'bg-red-100 text-red-700',
  high: 'bg-orange-100 text-orange-700',
  medium: 'bg-blue-100 text-blue-700',
  low: 'bg-gray-100 text-gray-600',
};

const formatHours = (h) => {
  if (h == null) return '—';
  if (h < 1) return `${Math.round(h * 60)}m`;
  if (h < 48) return `${h}h`;
  return `${Math.round((h / 24) * 10) / 10}d`;
};

const rateBarColor = (rate) =>
  rate >= 90 ? 'bg-green-500' : rate >= 70 ? 'bg-amber-500' : 'bg-red-500';

const rateTextColor = (rate) =>
  rate >= 90 ? 'text-green-500' : rate >= 70 ? 'text-amber-500' : 'text-red-500';

const TicketClosureRate = () => {
  const [days, setDays] = useState(30);

  const { data, isLoading, isFetching, isError, refetch } = useQuery({
    queryKey: ['ticket-closure-rate', days],
    queryFn: () => superAdminAPI.getTicketClosureRate({ days }),
    refetchInterval: 120000,
  });

  // Axios → res.data (server body) → body.data
  const body = data?.data?.data || {};
  const stats = body.stats || {};
  const byPriority = Array.isArray(body.byPriority) ? body.byPriority : [];
  const byOrganization = Array.isArray(body.byOrganization) ? body.byOrganization : [];
  const [orgFilters, setOrgFilters] = useState({ ...EMPTY_REPORT_FILTERS, band: '' });
  const orgRows = filterReportRows(byOrganization, orgFilters, {
    searchOf: (o) => [o.organizationName],
  }).filter((o) =>
    !orgFilters.band
    || (orgFilters.band === 'high' && o.closureRate >= 90)
    || (orgFilters.band === 'mid' && o.closureRate >= 70 && o.closureRate < 90)
    || (orgFilters.band === 'low' && o.closureRate < 70));

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex-1 basis-80">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <HiOutlineClipboardCheck className="w-7 h-7 text-primary-500" />
            Ticket Closure Rate
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Out of all tickets raised, how many have been solved
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-xs font-medium text-gray-500">Show last</span>
          <div className="flex flex-wrap gap-1 bg-gray-100 rounded-xl p-1">
            {PERIODS.map((p) => (
              <button
                key={p}
                onClick={() => setDays(p)}
                className={`px-3.5 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  days === p ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {periodLabel(p)}
              </button>
            ))}
          </div>
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary-500 text-white text-sm font-medium
                       hover:bg-primary-600 shadow-lg shadow-primary-500/25 disabled:opacity-50"
          >
            <HiOutlineRefresh className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      <ReportGuide
        id="ticket-closure"
        summary="Shows what share of tickets got solved. Green (90%+) is good, amber (70–89%) needs a look, red (below 70%) needs action."
        points={[
          ["Closure rate", "(Resolved + closed) ÷ all tickets in the period."],
          ["Resolved", "Fixed by the team."],
          ["Closed", "Fixed and fully closed."],
          ["Still open", "Not solved yet."],
        ]}
      />

      {/* Error banner — otherwise a failed request renders as all zeros */}
      {isError && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center justify-between">
          <p className="text-sm text-red-700">Failed to load the closure report — please try again.</p>
          <button
            onClick={() => refetch()}
            className="ml-4 px-3 py-1.5 rounded-lg bg-red-100 text-red-700 text-sm font-medium hover:bg-red-200 flex-shrink-0"
          >
            Retry
          </button>
        </div>
      )}

      {/* Hero closure rate + KPI tiles */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Hero rate */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 lg:col-span-2">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Closure Rate</p>
          <p className={`text-5xl font-bold mt-3 ${rateTextColor(stats.closureRate ?? 0)}`}>
            {stats.closureRate ?? 0}%
          </p>
          <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden mt-4">
            <div
              className={`h-full rounded-full ${rateBarColor(stats.closureRate ?? 0)}`}
              style={{ width: `${Math.min(100, stats.closureRate ?? 0)}%` }}
            />
          </div>
          <p className="text-xs text-gray-400 mt-3">
            {stats.resolvedTickets ?? 0} of {stats.totalTickets ?? 0} tickets resolved
            (last {body.windowDays ?? days} days)
          </p>
        </div>

        {/* KPI tiles */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Resolved</p>
          <p className="text-3xl font-bold text-green-500 mt-2">{stats.resolvedTickets ?? 0}</p>
          <p className="text-xs text-gray-400 mt-1">Tickets marked resolved</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Closed</p>
          <p className="text-3xl font-bold text-primary-500 mt-2">{stats.closedTickets ?? 0}</p>
          <p className="text-xs text-gray-400 mt-1">Fully closed ({stats.closedRate ?? 0}% closed rate)</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Still Open</p>
          <p className={`text-3xl font-bold mt-2 ${(stats.openTickets ?? 0) === 0 ? 'text-green-500' : 'text-amber-500'}`}>
            {stats.openTickets ?? 0}
          </p>
          <p className="text-xs text-gray-400 mt-1">Avg resolution {formatHours(stats.avgResolutionHours)}</p>
        </div>
      </div>

      {/* Closure rate by priority */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
          <h2 className="text-sm font-semibold text-gray-700">Closure Rate by Priority</h2>
          {byPriority.length > 0 && (
            <ExportMenu
              fileName={`closure-rate-by-priority-${days}d`}
              getRows={() => byPriority}
              columns={[
                { header: 'Priority', value: (p) => PRIORITY_LABELS[p.priority] || p.priority },
                { header: 'Total', value: (p) => p.total },
                { header: 'Resolved', value: (p) => p.resolved },
                { header: 'Closed', value: (p) => p.closed },
                { header: 'Open', value: (p) => p.open },
                { header: 'Closure Rate %', value: (p) => p.closureRate },
                { header: 'Avg Resolution', value: (p) => formatHours(p.avgResolutionHours) },
              ]}
            />
          )}
        </div>
        {isLoading ? (
          <div className="p-8 text-center text-gray-400 text-sm">Loading priority breakdown…</div>
        ) : byPriority.length === 0 ? (
          <div className="p-8 text-center text-gray-400 text-sm">
            No tickets in this period
          </div>
        ) : (
        <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wider text-gray-500">
                  <th className="px-4 py-3">Priority</th>
                  <th className="px-4 py-3">Total</th>
                  <th className="px-4 py-3">Resolved</th>
                  <th className="px-4 py-3">Closed</th>
                  <th className="px-4 py-3">Open</th>
                  <th className="px-4 py-3">Closure Rate</th>
                  <th className="px-4 py-3">Avg Resolution</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {byPriority.map((p) => (
                  <tr key={p.priority} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${PRIORITY_COLORS[p.priority] || 'bg-gray-100 text-gray-600'}`}>
                        {PRIORITY_LABELS[p.priority] || p.priority}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-semibold text-gray-900">{p.total}</td>
                    <td className="px-4 py-3 text-green-600 font-medium">{p.resolved}</td>
                    <td className="px-4 py-3 text-gray-600">{p.closed}</td>
                    <td className="px-4 py-3 text-amber-600 font-medium">{p.open}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${rateBarColor(p.closureRate)}`}
                            style={{ width: `${Math.min(100, p.closureRate)}%` }}
                          />
                        </div>
                        <span className={`font-medium ${rateTextColor(p.closureRate)}`}>{p.closureRate}%</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 font-medium text-gray-900">{formatHours(p.avgResolutionHours)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Closure rate by organization */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
          <h2 className="text-sm font-semibold text-gray-700">Closure Rate by Organization</h2>
          {orgRows.length !== byOrganization.length && (
            <span className="text-xs text-gray-400">{orgRows.length} of {byOrganization.length} shown</span>
          )}
        </div>
        {byOrganization.length > 0 && (
          <ReportFilterBar
            filters={orgFilters}
            onChange={setOrgFilters}
            showPriority={false}
            searchPlaceholder="Search organization…"
            extraActive={!!orgFilters.band}
            onResetExtra={() => setOrgFilters((f) => ({ ...f, band: '' }))}
            exportProps={{
              fileName: `closure-rate-by-organization-${days}d`,
              getRows: () => orgRows,
              columns: [
                { header: 'Organization', value: (o) => o.organizationName },
                { header: 'Total', value: (o) => o.total },
                { header: 'Resolved', value: (o) => o.resolved },
                { header: 'Closed', value: (o) => o.closed },
                { header: 'Open', value: (o) => o.open },
                { header: 'Closure Rate %', value: (o) => o.closureRate },
              ],
            }}
          >
            <FilterSelect
              value={orgFilters.band}
              onChange={(v) => setOrgFilters((f) => ({ ...f, band: v }))}
              allLabel="Any Closure Rate"
              options={[['high', '90% and above'], ['mid', '70% – 89%'], ['low', 'Below 70%']]}
            />
          </ReportFilterBar>
        )}
        {isLoading ? (
          <div className="p-8 text-center text-gray-400 text-sm">Loading organization breakdown…</div>
        ) : byOrganization.length === 0 ? (
          <div className="p-8 text-center text-gray-400 text-sm">
            No tickets in this period
          </div>
        ) : orgRows.length === 0 ? (
          <div className="p-8 text-center text-gray-400 text-sm">No organizations match these filters</div>
        ) : (
        <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wider text-gray-500">
                  <th className="px-4 py-3">Organization</th>
                  <th className="px-4 py-3">Total</th>
                  <th className="px-4 py-3">Resolved</th>
                  <th className="px-4 py-3">Closed</th>
                  <th className="px-4 py-3">Open</th>
                  <th className="px-4 py-3">Closure Rate</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {orgRows.map((o) => (
                  <tr key={o.organizationName} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">{o.organizationName}</td>
                    <td className="px-4 py-3 font-semibold text-gray-900">{o.total}</td>
                    <td className="px-4 py-3 text-green-600 font-medium">{o.resolved}</td>
                    <td className="px-4 py-3 text-gray-600">{o.closed}</td>
                    <td className="px-4 py-3 text-amber-600 font-medium">{o.open}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${rateBarColor(o.closureRate)}`}
                            style={{ width: `${Math.min(100, o.closureRate)}%` }}
                          />
                        </div>
                        <span className={`font-medium ${rateTextColor(o.closureRate)}`}>{o.closureRate}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default TicketClosureRate;